import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import QuotaBadge from "../components/QuotaBadge";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import SpeakButton from "../components/SpeakButton";
import { api, formatError } from "../lib/api";
import { useSpeechRecognition } from "../lib/useSpeechRecognition";
import { speak, stopSpeaking } from "../lib/voice";
import { Send, Trash2, Sparkles, MessageCircleHeart, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

export default function Assistant() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const {
    supported: micSupported,
    listening,
    transcript,
    start: startMic,
    stop: stopMic,
    error: micError,
  } = useSpeechRecognition(lang);
  const [voiceReply, setVoiceReply] = useState(() => {
    try {
      return localStorage.getItem("sereno_voice_reply") === "1";
    } catch {
      return false;
    }
  });

  // Live-fill the input while user speaks
  useEffect(() => {
    if (transcript) setText(transcript);
  }, [transcript]);

  useEffect(() => {
    try {
      localStorage.setItem("sereno_voice_reply", voiceReply ? "1" : "0");
    } catch {
      // ignore
    }
  }, [voiceReply]);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/chat/history");
        setMessages(data.messages);
      } catch {
        // silent
      }
    })();
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const val = text.trim();
    if (!val || busy) return;
    setBusy(true);
    setError("");
    setMessages((m) => [...m, { role: "user", text: val, created_at: new Date().toISOString() }]);
    setText("");
    try {
      const { data } = await api.post("/chat/send", { text: val });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: data.reply, created_at: new Date().toISOString() },
      ]);
      refresh(); // refresh usage counter
      if (voiceReply) {
        // Speak the reply using the user's chosen voice
        try {
          stopSpeaking();
          speak(data.reply, lang);
        } catch {
          // silent
        }
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete("/chat/history");
      setMessages([]);
      toast.success(lang === "fr" ? "Conversation effacée" : "Conversation cleared");
    } catch {
      toast.error(t("err_generic"));
    }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10" data-testid="assistant-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
            <MessageCircleHeart className="h-7 w-7" strokeWidth={2} />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
              {t("ask_sereno_short")}
            </p>
            <h1 className="font-serif text-3xl font-semibold text-[#101A42] sm:text-4xl">
              {t("ask_sereno")}
            </h1>
          </div>
        </div>
        {messages.length > 0 && (
          <Button
            variant="outline"
            onClick={clear}
            data-testid="chat-clear-btn"
            className="min-h-[44px] rounded-full border-2 border-[#101A42]/40 text-base text-[#101A42]"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("ask_clear")}
          </Button>
        )}
      </div>

      {messages.length === 0 && (
        <div
          data-testid="assistant-intro"
          className="mb-4 rounded-2xl border-2 border-[#A762B1]/25 bg-[#F8EFD9] p-6"
        >
          <p className="text-lg leading-relaxed text-[#101A42]">{t("ask_sereno_intro")}</p>
        </div>
      )}

      <QuotaBadge
        kind="chat"
        limit={3}
        usage={user?.usage}
        lang={lang}
        testid="chat-quota"
      />

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className="mb-4 max-h-[55vh] min-h-[280px] overflow-y-auto rounded-2xl border-2 border-[#E8DFC7] bg-white p-4 sm:p-6"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${m.role}-${i}`}
            className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-base leading-relaxed ${
                m.role === "user"
                  ? "bg-[#101A42] text-white"
                  : "bg-[#F8EFD9] text-[#101A42]"
              }`}
            >
              {m.role === "assistant" && (
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#A762B1]">
                  <Sparkles className="h-3.5 w-3.5" />
                  Sereno
                </div>
              )}
              <p className="whitespace-pre-line">{m.text}</p>
              {m.role === "assistant" && (
                <div className="mt-3">
                  <SpeakButton text={m.text} testid={`chat-msg-speak-${i}`} />
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="chat-thinking">
            <div className="rounded-2xl bg-[#F8EFD9] px-4 py-3 text-base italic text-[#4A4A4A]">
              {t("ask_thinking")}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-xl border-2 border-[#B91C1C]/30 bg-[#B91C1C]/10 p-3 text-[#8a2020]">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Textarea
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={listening ? t("mic_listening") : t("ask_sereno_placeholder")}
          rows={2}
          className="flex-1 rounded-2xl border-2 text-lg"
        />
        <div className="flex gap-2">
          {micSupported ? (
            <Button
              type="button"
              onClick={listening ? stopMic : startMic}
              data-testid={listening ? "chat-mic-stop-btn" : "chat-mic-start-btn"}
              aria-label={listening ? t("mic_stop") : t("mic_start")}
              className={`min-h-[56px] rounded-full px-5 text-lg font-semibold ${
                listening
                  ? "bg-[#B91C1C] text-white hover:bg-[#DC2626] animate-pulse"
                  : "bg-[#101A42] text-white hover:bg-[#1A2650]"
              }`}
            >
              {listening ? (
                <>
                  <MicOff className="mr-2 h-5 w-5" />
                  {t("mic_stop")}
                </>
              ) : (
                <>
                  <Mic className="mr-2 h-5 w-5" />
                  {t("mic_start")}
                </>
              )}
            </Button>
          ) : null}
          <Button
            onClick={send}
            disabled={busy || !text.trim()}
            data-testid="chat-send-btn"
            className="min-h-[56px] rounded-full bg-[#A762B1] px-6 text-lg font-semibold text-white hover:bg-[#B775C0]"
          >
            <Send className="mr-2 h-5 w-5" />
            {t("ask_send")}
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            const next = !voiceReply;
            setVoiceReply(next);
            if (!next) stopSpeaking();
          }}
          data-testid="voice-reply-toggle"
          className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-base font-semibold transition-colors ${
            voiceReply
              ? "border-[#A762B1] bg-[#A762B1] text-white"
              : "border-[#101A42]/30 bg-white text-[#101A42] hover:border-[#101A42]"
          }`}
        >
          {voiceReply ? (
            <>
              <Volume2 className="h-4 w-4" />
              {t("voice_reply_on")}
            </>
          ) : (
            <>
              <VolumeX className="h-4 w-4" />
              {t("voice_reply_off")}
            </>
          )}
        </button>
        {!micSupported && (
          <span className="text-sm text-[#4A4A4A]" data-testid="mic-not-supported">
            {t("mic_not_supported")}
          </span>
        )}
        {micError && listening && (
          <span className="text-sm text-[#B91C1C]">Mic: {micError}</span>
        )}
      </div>

      <p className="mt-4 text-sm text-[#4A4A4A]">{t("ask_disclaimer")}</p>
    </div>
  );
}
